import { HelpOutline, SvgIconComponent } from '@mui/icons-material';
import {
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { FC, ReactNode } from 'react';

/**
 * セクション 1 つ分の枠。白基調・角丸なしで下線だけを引き、縦に積み重ねる。
 * id はサイドナビのジャンプ先アンカーで、scrollMarginTop はジャンプ時の上の余白。
 */
export const SectionCard: FC<{
  id: string;
  title: string;
  description?: string;
  icon: SvgIconComponent;
  action?: ReactNode;
  children: ReactNode;
}> = ({
  id, title, description, icon: Icon, action, children,
}) => (
  <Paper
    id={id}
    variant="outlined"
    sx={{
      borderRadius: 0,
      borderLeft: 0,
      borderRight: 0,
      borderTop: 0,
      p: { xs: 2, md: 3 },
      scrollMarginTop: 16,
      bgcolor: 'background.paper',
      boxShadow: 'none',
    }}
  >
    <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          // LogTheme の palette.primary には lighter が無いので main から起こす
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          color: 'primary.main',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4 }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{
            fontSize: 13, color: 'text.secondary', mt: 0.25, lineHeight: 1.6,
          }}
          >
            {description}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

/** 入力欄 1 つ分。小さなラベルと、必要ならヘルプアイコンを上に置く */
export const Field: FC<{
  label: string;
  hint?: string;
  width?: number | string;
  children: ReactNode;
}> = ({
  label, hint, width = 200, children,
}) => (
  <Box sx={{ width }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow>
          <HelpOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
        </Tooltip>
      )}
    </Stack>
    {children}
  </Box>
);

/**
 * ON/OFF 1 件分。ラベルまで含めて 1 つの label にする。
 *
 * Why not: デザインは枠の onClick と Switch の onChange を併用しているが、
 * Switch を直接クリックすると両方が走って 2 回反転する。ここでは label 経由の
 * 1 経路だけにして、ヘルプアイコンは label の外に出してクリックで反転しないようにした。
 */
export const ToggleRow: FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({
  label, hint, checked, onChange,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      pr: hint ? 1 : 0,
      borderRadius: 1.5,
      border: 1,
      borderColor: checked ? 'primary.main' : 'divider',
      bgcolor: checked ? 'action.selected' : 'transparent',
      transition: 'all .15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <FormControlLabel
      control={(
        <Switch
          size="small"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      )}
      label={<Typography sx={{ fontSize: 12.5, lineHeight: 1.4 }}>{label}</Typography>}
      sx={{
        flex: 1, m: 0, px: 1.25, py: 1, minWidth: 0,
      }}
    />
    {hint && (
      <Tooltip title={hint} arrow>
        <HelpOutline sx={{ fontSize: 15, color: 'text.disabled' }} />
      </Tooltip>
    )}
  </Stack>
);
